import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { prettyClubName } from '../../components/TeamSelector/TeamSelector';
import { getImgSrc } from '../../helpers/getImageSrc';
import defaultPlayerImg from '../../img/defaultPlayer.jpg';
import './Copa.css';
import { simularPartido } from './matchSim';
import { fuerzaEquipo } from './dealEngine';

import { RONDAS, EQUIPOS, armarCuadro, cruzar, resolverCruce, indiceDeMiCruce, miRivalEn } from './bracket';

const ICONO = { gol: '⚽', gol_anulado: '🚫', amarilla: '🟨', roja: '🟥' };

const backendURL = process.env.REACT_APP_BACKEND_URL || 'https://api.footy-games.com.ar';

/** Convierte el plantel propio (índice de slot -> jugador) en las líneas que usa el simulador. */
function plantelALineas(plantel, slots) {
    const lineas = { gk: [], defensa: [], medio: [], ataque: [] };
    const aLinea = { GK: 'gk', CB: 'defensa', LB: 'defensa', RB: 'defensa',
        CDM: 'medio', CM: 'medio', CAM: 'medio', LM: 'medio', RM: 'medio',
        LW: 'ataque', RW: 'ataque', ST: 'ataque' };
    Object.entries(plantel).forEach(([i, j]) => {
        const linea = aLinea[slots[i]] || 'medio';
        // El jugador entero, no solo el nombre: la presentacion muestra foto y valor igual que
        // el rival, y el simulador ya sabe leer objetos.
        lineas[linea].push({ nombre: j.nombre, img: j.img, valor: j.valor });
    });
    return lineas;
}

export default function Copa({ plantel, slots, formacion, onReiniciar }) {
    const [cuadro, setCuadro] = useState(null);      // cruces por ronda
    const [errorRivales, setErrorRivales] = useState(null);
    const [ronda, setRonda] = useState(0);
    const [fase, setFase] = useState('presentacion');   // presentacion | jugando | penales | resultado
    const [partido, setPartido] = useState(null);
    const [minuto, setMinuto] = useState(0);
    const [penalesVistos, setPenalesVistos] = useState(0);
    const [historial, setHistorial] = useState([]);
    const timers = useRef([]);

    // Los rivales salen de la base: un once sorteado de cada club, distinto en cada partida.
    // Son 31 porque el cuadro arranca en dieciseisavos, con 32 equipos contando el tuyo.
    useEffect(() => {
        let vivo = true;
        fetch(`${backendURL}/deal/rivales?cantidad=${EQUIPOS - 1}`)
            .then(r => r.json())
            .then(d => {
                if (!vivo) return;
                if (!d.rivales?.length) throw new Error('No se pudieron armar los rivales');
                const { participantes } = armarCuadro({ nombre: 'Tu equipo', fuerza: miFuerza, lineas: miEquipo.plantel }, d.rivales);
                setCuadro([cruzar(participantes)]);
            })
            .catch(e => vivo && setErrorRivales(e.message));
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const miFuerza = fuerzaEquipo(plantel);
    const crucesRonda = cuadro?.[ronda] || null;
    const rival = crucesRonda ? miRivalEn(crucesRonda) : null;
    // useMemo y no un objeto suelto: se recreaba en cada render, con lo cual arrancar() cambiaba
    // de identidad continuamente y el useCallback no servia de nada.
    const miEquipo = useMemo(
        () => ({ nombreCorto: 'Tu equipo', fuerza: miFuerza, plantel: plantelALineas(plantel, slots) }),
        [miFuerza, plantel, slots]
    );

    const limpiar = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    useEffect(() => limpiar, []);

    const arrancar = useCallback(() => {
        if (!rival) return;
        const r = simularPartido(miEquipo, { nombreCorto: prettyClubName(rival.club), fuerza: rival.fuerza, plantel: rival.lineas });
        setPartido(r);
        setMinuto(0);
        setPenalesVistos(0);
        setFase('jugando');
    }, [miEquipo, rival]);

    // Reloj del partido: 90' (o 120' si hay alargue) en ~14 segundos.
    useEffect(() => {
        if (fase !== 'jugando' || !partido) return;
        const tope = partido.alargue ? 120 : 90;
        const paso = 14000 / tope;
        const t = setInterval(() => {
            setMinuto(m => {
                if (m >= tope) {
                    clearInterval(t);
                    // Si hubo empate tras el alargue, se pasa a la tanda; si no, al resultado.
                    setFase(partido.penales ? 'penales' : 'resultado');
                    return tope;
                }
                return m + 1;
            });
        }, paso);
        return () => clearInterval(t);
    }, [fase, partido]);

    // Los penales se muestran de a uno, con pausa para que se vea cada remate.
    useEffect(() => {
        if (fase !== 'penales' || !partido?.penales) return;
        if (penalesVistos >= partido.penales.tiros.length) {
            const t = setTimeout(() => setFase('resultado'), 1200);
            timers.current.push(t);
            return;
        }
        const t = setTimeout(() => setPenalesVistos(n => n + 1), 1100);
        timers.current.push(t);
    }, [fase, partido, penalesVistos]);

    const seguir = () => {
        const gano = partido.ganaLocal;
        setHistorial(h => [...h, { ronda: RONDAS[ronda], rival, partido, gano }]);

        // Se resuelven TODOS los cruces de la ronda, no solo el tuyo: de los otros sale el rival
        // que te va a tocar despues, que es lo que hace que el cuadro tenga sentido.
        const miIdx = indiceDeMiCruce(crucesRonda);
        const resueltos = crucesRonda.map((c, i) => {
            if (i === miIdx) {
                const ganador = gano ? (c.a.tipo === 'propio' ? c.a : c.b) : (c.a.tipo === 'propio' ? c.b : c.a);
                return { ...c, ganador, resultado: { marcador: partido.marcador, penales: partido.penales ? partido.penales.marcador : null } };
            }
            return resolverCruce(c);
        });
        const nuevoCuadro = [...cuadro];
        nuevoCuadro[ronda] = resueltos;

        if (!gano || ronda === RONDAS.length - 1) { setCuadro(nuevoCuadro); setFase('fin'); return; }

        nuevoCuadro[ronda + 1] = cruzar(resueltos.map(c => c.ganador));
        setCuadro(nuevoCuadro);
        setRonda(r => r + 1);
        setPartido(null);
        setFase('presentacion');
    };

    const eventosVistos = (partido?.eventos || []).filter(e => e.minuto <= minuto);
    // Los goles anulados por VAR se muestran pero no suman: por eso se filtra por tipo.
    const marcadorVivo = [
        eventosVistos.filter(e => e.tipo === 'gol' && e.equipo === 'local').length,
        eventosVistos.filter(e => e.tipo === 'gol' && e.equipo === 'visitante').length,
    ];

    if (errorRivales) return <div className="copa"><p className="copa-sub">{errorRivales}</p></div>;
    if (!cuadro) return <div className="copa"><p className="copa-sub">Sorteando el cuadro…</p></div>;

    // ── Fin del recorrido ───────────────────────────────────────────────────
    if (fase === 'fin') {
        const campeon = historial.length === RONDAS.length && historial[historial.length - 1].gano;
        return (
            <div className="copa">
                <h2 className={campeon ? 'copa-campeon' : 'copa-eliminado'}>
                    {campeon ? '🏆 Campeones' : `Eliminados en ${historial[historial.length - 1].ronda}`}
                </h2>
                <p className="copa-sub">Tu equipo valía <strong>€{miFuerza}M</strong></p>
                <ol className="copa-camino">
                    {historial.map((h, i) => (
                        <li key={i} className={h.gano ? 'gano' : 'perdio'}>
                            <span className="copa-camino-ronda">{h.ronda}</span>
                            <span className="copa-camino-rival">{h.rival ? prettyClubName(h.rival.club) : '—'} <small>€{h.rival?.fuerza}M</small></span>
                            <strong>{h.partido.marcador.join(' - ')}</strong>
                            {h.partido.penales && <small className="copa-pen">pen {h.partido.penales.marcador.join('-')}</small>}
                        </li>
                    ))}
                </ol>
                <button className="copa-btn" onClick={onReiniciar}>Jugar otra</button>
            </div>
        );
    }

    // ── Presentación de los dos equipos ─────────────────────────────────────
    if (fase === 'presentacion') {
        return (
            <div className="copa">
                <div className="copa-cabecera">
                    <span className="copa-ronda">{RONDAS[ronda]}</span>
                    <span className="copa-progreso">{ronda + 1} de {RONDAS.length}</span>
                </div>
                <Cuadro cuadro={cuadro} rondaActual={ronda} />
                <div className="copa-presentacion">
                    <Formacion titulo="Tu equipo" sub={formacion} fuerza={miFuerza} lineas={miEquipo.plantel} propio />
                    <div className="copa-vs">VS</div>
                    <Formacion titulo={prettyClubName(rival.club)} sub={`Once sorteado · ${rival.once.length} jugadores`}
                        fuerza={rival.fuerza} lineas={rival.lineas} escudo={rival.club} />
                </div>
                <button className="copa-btn" onClick={arrancar}>Que empiece el partido</button>
            </div>
        );
    }

    // ── Partido en vivo y penales ───────────────────────────────────────────
    // Va en un modal a pantalla completa: en mobile, con el cuadro y la plantilla alrededor, no
    // se notaba que habia un partido corriendo. Acá no compite con nada.
    return (
        <div className="copa-modal" role="dialog" aria-label={`Partido: ${RONDAS[ronda]}`}>
          <div className="copa-modal-caja">
            <div className="copa-cabecera">
                <span className="copa-ronda">{RONDAS[ronda]}</span>
                <span className={`copa-reloj${fase === 'jugando' ? ' corriendo' : ''}`}>
                    {fase === 'penales' ? 'PENALES' : `${minuto}'`}
                </span>
            </div>

            {fase === 'jugando' && (
                <div className="copa-barra-tiempo">
                    <span style={{ width: `${(minuto / (partido?.alargue ? 120 : 90)) * 100}%` }} />
                </div>
            )}

            <div className="copa-marcador">
                <span className="copa-equipo">Tu equipo</span>
                <strong>{marcadorVivo[0]} - {marcadorVivo[1]}</strong>
                <span className="copa-equipo"><Escudo club={rival.club} size={30} /> {prettyClubName(rival.club)}</span>
            </div>
            {partido?.alargue && minuto > 90 && <p className="copa-aviso">Tiempo suplementario</p>}

            <ul className="copa-eventos">
                {eventosVistos.map((e, i) => (
                    <li key={i} className={`${e.equipo === 'local' ? 'propio' : 'rival'} ev-${e.tipo}`}>
                        <span className="copa-min">{e.minuto}'</span>
                        <span className="copa-icono">{ICONO[e.tipo]}</span>
                        <span className="copa-autor">{e.autor}</span>
                        {e.tipo === 'gol_anulado' && <span className="copa-detalle">anulado — {e.motivo}</span>}
                        {e.tipo === 'roja' && e.doble && <span className="copa-detalle">doble amarilla</span>}
                    </li>
                ))}
                {!eventosVistos.length && <li className="copa-sin">Todavía no pasó nada…</li>}
            </ul>

            {fase === 'penales' && partido.penales && (
                <div className="copa-penales">
                    <h3>Definición por penales</h3>
                    <div className="copa-tanda">
                        {partido.penales.tiros.slice(0, penalesVistos).map((t, i) => (
                            <div key={i} className={`copa-penal ${t.equipo === 'local' ? 'propio' : 'rival'} ${t.convierte ? 'gol' : 'errado'}`}>
                                <span className="copa-penal-icono">{t.convierte ? '⚽' : '✖'}</span>
                                <span className="copa-penal-nombre">{t.pateador}</span>
                            </div>
                        ))}
                    </div>
                    <p className="copa-penal-marcador">
                        {(() => {
                            const hasta = partido.penales.tiros.slice(0, penalesVistos);
                            const g = (lado) => hasta.filter(t => t.equipo === lado && t.convierte).length;
                            return `${g('local')} - ${g('visitante')}`;
                        })()}
                    </p>
                </div>
            )}

            {fase === 'resultado' && (
                <div className="copa-resultado">
                    <h3 className={partido.ganaLocal ? 'copa-campeon' : 'copa-eliminado'}>
                        {partido.ganaLocal ? 'Ganaste' : 'Quedaste afuera'}
                    </h3>
                    <p>{partido.marcador.join(' - ')}
                        {partido.penales && ` (penales ${partido.penales.marcador.join('-')})`}</p>
                    <button className="copa-btn" onClick={seguir}>
                        {partido.ganaLocal && ronda < RONDAS.length - 1 ? 'Siguiente ronda' : 'Ver el recorrido'}
                    </button>
                </div>
            )}
          </div>
        </div>
    );
}

/** Escudo del club, de la misma carpeta que usa el tablero del tateti. */
function Escudo({ club, size = 34 }) {
    let img = null;
    if (club) { try { img = require(`../../img/teams/${club}.jpg`); } catch (e) { img = null; } }
    if (!img) return <span className="copa-escudo copa-escudo--vacio" style={{ width: size, height: size }}>⚽</span>;
    return <img className="copa-escudo" src={img} alt="" style={{ width: size, height: size }} />;
}

/**
 * El cuadro: todas las llaves, no solo las tuyas. Se ve de donde va a salir tu proximo rival —el
 * ganador de otro cruce— en vez de saberlo de antemano, que es lo que hace que un cuadro sea un
 * cuadro. La ronda que estas jugando queda resaltada.
 */
function Cuadro({ cuadro, rondaActual }) {
    return (
        <div className="copa-cuadro-wrap">
            <div className="copa-cuadro">
                {RONDAS.map((nombre, r) => {
                    const cruces = cuadro[r];
                    return (
                        <div key={nombre} className={`copa-col${r === rondaActual ? ' actual' : ''}`}>
                            <span className="copa-col-tit">{nombre}</span>
                            <div className="copa-col-cruces">
                                {cruces
                                    ? cruces.map((c, i) => <CruceChico key={i} cruce={c} />)
                                    : Array.from({ length: 16 / Math.pow(2, r) }).map((_, i) => (
                                        <div key={i} className="copa-cruce-chico vacio" />
                                    ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function CruceChico({ cruce }) {
    const mio = cruce.a?.tipo === 'propio' || cruce.b?.tipo === 'propio';
    const lado = (e) => {
        if (!e) return <span className="copa-lado libre">—</span>;
        const gano = cruce.ganador === e;
        return (
            <span className={`copa-lado${e.tipo === 'propio' ? ' propio' : ''}${cruce.ganador && !gano ? ' eliminado' : ''}`}>
                {e.tipo === 'propio' ? <span className="copa-yo">TU</span> : <Escudo club={e.club} size={18} />}
            </span>
        );
    };
    return (
        <div className={`copa-cruce-chico${mio ? ' mio' : ''}${cruce.ganador ? ' jugado' : ''}`}
            title={etiquetaCruce(cruce)}>
            {lado(cruce.a)}
            <span className="copa-cruce-marcador">
                {cruce.resultado ? cruce.resultado.marcador.join('-') : 'vs'}
            </span>
            {lado(cruce.b)}
        </div>
    );
}

function etiquetaCruce(c) {
    const nom = (e) => !e ? 'libre' : (e.tipo === 'propio' ? 'Tu equipo' : prettyClubName(e.club));
    if (!c.resultado) return `${nom(c.a)} vs ${nom(c.b)}`;
    const pen = c.resultado.penales ? ` (pen ${c.resultado.penales.join('-')})` : '';
    return `${nom(c.a)} ${c.resultado.marcador.join('-')} ${nom(c.b)}${pen}`;
}

function Formacion({ titulo, sub, fuerza, lineas, propio = false, escudo = null }) {
    const filas = ['ataque', 'medio', 'defensa', 'gk'];
    return (
        <div className={`copa-formacion${propio ? ' propia' : ''}`}>
            <div className="copa-formacion-tit">
                <Escudo club={escudo} size={38} />
                <div>
                    <h3>{titulo}</h3>
                    <p className="copa-formacion-sub">{sub}</p>
                </div>
            </div>
            <div className="copa-cancha-mini">
                {filas.map(clave => (
                    <div key={clave} className="copa-linea">
                        {(lineas[clave] || []).map((j, i) => {
                            const nombre = typeof j === 'string' ? j : j.nombre;
                            const foto = typeof j === 'string' ? null : j.img;
                            const valor = typeof j === 'string' ? null : j.valor;
                            return (
                                <span key={i} className="copa-jugador" title={valor ? `${nombre} — €${Math.round(valor / 1e6)}M` : nombre}>
                                    <img src={getImgSrc(foto) || defaultPlayerImg} alt=""
                                        onError={e => { e.target.src = defaultPlayerImg; }} />
                                    <b>{nombre.split(' ').slice(-1)[0]}</b>
                                    {valor ? <em>{Math.round(valor / 1e6)}M</em> : null}
                                </span>
                            );
                        })}
                    </div>
                ))}
            </div>
            <p className="copa-fuerza">Valor del once <strong>€{fuerza}M</strong></p>
        </div>
    );
}
